import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { karaokePlayEntranceTransition } from '@/constants/storyResourcesEntrance';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import { useStoryAudio } from '@/context/StoryAudioContext';
import { cn } from '@/utils/cn';
import styles from './KaraokePlayer.module.scss';
import type { KaraokePlayerProps, WordTimestamp } from './KaraokePlayer.types';

/**
 * Espacio entre el borde inferior del header y el ancla (bloque de karaoke) al
 * pulsar play. Debe ser ≥ `KARAOKE_VIEW_MARGIN_TOP_PX`: si el karaoke queda más
 * pegado al header, las primeras palabras caen en la zona de corrección y
 * `scrollLayoutIfWordClipped` las empuja abajo justo tras el snap (salto feo).
 * Por eso el mínimo seguro es exactamente ese margen.
 */
const KARAOKE_LAYOUT_SCROLL_GAP_PX = 28;
/**
 * Por encima de esta línea (bajo el header) ya corregimos scroll. Más alto = más tolerancia
 * (no mover si la palabra sigue cómoda en la parte superior).
 */
const KARAOKE_VIEW_MARGIN_TOP_PX = 28;
/**
 * Por debajo de esta línea (desde el pie del viewport) consideramos “demasiado abajo”.
 * Más bajo = menos anticipación (el scroll espera a que el texto baje más).
 */
const KARAOKE_VIEW_MARGIN_BOTTOM_PX = 140;
/** Tras snap suave al play: no aplicar scroll por palabra hasta entonces (evita choque de animaciones). */
const KARAOKE_PLAY_SMOOTH_SUPPRESS_MS = 520;

function scrollKaraokeBlockBelowHeader(
  karaokeEl: HTMLElement | null,
  behavior: ScrollBehavior = 'smooth'
) {
  if (!karaokeEl) return;
  // closest() returns Element; tighten para los callers de abajo que tipan HTMLElement.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const scrollRoot = karaokeEl.closest('[data-story-scroll-container]') as HTMLElement | null;
  if (!scrollRoot) return;
  const header = scrollRoot.querySelector('header');
  const headerBottom = header?.getBoundingClientRect().bottom ?? 88;
  const targetTop = headerBottom + KARAOKE_LAYOUT_SCROLL_GAP_PX;
  // Anclamos el propio bloque de karaoke justo bajo el header.
  const anchorTop = karaokeEl.getBoundingClientRect().top;
  const delta = anchorTop - targetTop;
  if (Math.abs(delta) < 3) return;
  scrollRoot.scrollBy({ top: delta, behavior });
}

/**
 * Solo mueve el scroll del layout si la palabra queda tapada arriba (header) o abajo (barra).
 * `auto` evita encadenar varios smooth y el efecto “sube y baja”.
 */
function scrollLayoutIfWordClipped(wordEl: HTMLElement | null, scrollRoot: HTMLElement | null) {
  if (!wordEl || !scrollRoot) return;
  const header = scrollRoot.querySelector('header');
  const headerBottom = header?.getBoundingClientRect().bottom ?? 88;
  const rootRect = scrollRoot.getBoundingClientRect();
  const topLimit = headerBottom + KARAOKE_VIEW_MARGIN_TOP_PX;
  const bottomLimit = rootRect.bottom - KARAOKE_VIEW_MARGIN_BOTTOM_PX;
  if (bottomLimit <= topLimit + 24) return;

  const r = wordEl.getBoundingClientRect();

  if (r.top < topLimit) {
    const delta = r.top - topLimit;
    if (delta < -2) scrollRoot.scrollBy({ top: delta, behavior: 'auto' });
  } else if (r.bottom > bottomLimit) {
    const delta = r.bottom - bottomLimit;
    if (delta > 2) scrollRoot.scrollBy({ top: delta, behavior: 'auto' });
  }
}

export function KaraokePlayer({
  audioUrl,
  transcriptUrl,
  className,
  onAudioFinished,
  autoPlay = false,
  initiallyListened = false,
}: KaraokePlayerProps) {
  const { t } = useTranslation();
  const audioContext = useStoryAudio();
  const pageReady = usePageTransitionEntrance();
  const reducedMotion = Boolean(useReducedMotion());
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const karaokeRootRef = React.useRef<HTMLDivElement>(null);
  const wordRefs = React.useRef<(HTMLSpanElement | null)[]>([]);
  const suppressWordLayoutScrollUntilRef = React.useRef(0);
  const onAudioFinishedRef = React.useRef(onAudioFinished);
  onAudioFinishedRef.current = onAudioFinished;
  /**
   * Ref del contexto para que los listeners del elemento <audio> (que se
   * montan con `[audioUrl]`) puedan llamar a `markAudioFinished()` sin
   * depender directamente del valor del contexto en su closure.
   */
  const audioContextRef = React.useRef(audioContext);
  audioContextRef.current = audioContext;
  /**
   * Pasa a `true` la primera vez que el audio termina y nunca se resetea
   * mientras el `KaraokePlayer` esté montado: a partir de entonces, una
   * pausa manual del alumno tendrá que tratarse como «ya escuchado» para
   * volver a mostrar «Empezar actividades».
   */
  const hasBeenListenedRef = React.useRef(initiallyListened);

  /**
   * Velocidad elegida en la barra inferior (PlaybackSpeedDrawer, vía
   * contexto). Se aplica aquí porque este componente posee el <audio>.
   * También en `defaultPlaybackRate`: un `load()` (el que dispara
   * handlePlayPause en iOS) resetea `playbackRate` a ese valor por spec.
   */
  const playbackSpeed = audioContext?.playbackSpeed ?? 1;
  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = playbackSpeed;
    audio.defaultPlaybackRate = playbackSpeed;
  }, [playbackSpeed]);

  const [timestamps, setTimestamps] = React.useState<WordTimestamp[]>([]);
  const [isLoadingTranscript, setIsLoadingTranscript] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [hasEnded, setHasEnded] = React.useState(false);

  // Diagnóstico en desarrollo: verificar que el audio es accesible y tiene CORS
  React.useEffect(() => {
    if (!audioUrl || !import.meta.env.DEV) return;
    fetch(audioUrl, { method: 'HEAD' })
      .then((r) => {
        const cors = r.headers.get('access-control-allow-origin');
        const contentType = r.headers.get('content-type');
        if (r.ok && cors && contentType?.includes('audio')) {
          console.log('[KaraokePlayer] Audio OK:', { status: r.status, cors, contentType });
        } else {
          console.warn('[KaraokePlayer] Audio puede fallar:', {
            status: r.status,
            cors: cors ?? 'NO CORS',
            contentType: contentType ?? 'NO content-type',
            audioUrl,
          });
        }
      })
      .catch((err: unknown) =>
        console.warn(
          '[KaraokePlayer] HEAD falló:',
          err instanceof Error ? err.message : err,
          { audioUrl }
        )
      );
  }, [audioUrl]);

  // Load transcript JSON — supports both inline JSON (starts with '[') and remote URL
  React.useEffect(() => {
    if (!transcriptUrl) return;
    setIsLoadingTranscript(true);

    if (transcriptUrl.trimStart().startsWith('[')) {
      // Inline JSON stored directly in the transcript field (generated by AI flow)
      try {
        const data = JSON.parse(transcriptUrl) as unknown;
        setTimestamps(Array.isArray(data) ? (data as WordTimestamp[]) : []);
      } catch {
        setTimestamps([]);
      } finally {
        setIsLoadingTranscript(false);
      }
    } else {
      // Remote URL — fetch as before (backward compatible)
      fetch(transcriptUrl)
        .then((r) => r.json())
        .then((data: unknown) => setTimestamps(Array.isArray(data) ? (data as WordTimestamp[]) : []))
        .catch(() => setTimestamps([]))
        .finally(() => setIsLoadingTranscript(false));
    }
  }, [transcriptUrl]);

  // Audio event listeners
  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const syncTime = () => setCurrentTime(audio.currentTime);

    const handlePlay = () => {
      setIsPlaying(true);
      setHasEnded(false);
      // Si el audio había terminado y el alumno lo vuelve a reproducir,
      // notificamos al `StoryAudioContext` para que la barra inferior
      // vuelva al estado 2 (icono play centrado, sin «Empezar actividades»).
      audioContextRef.current?.syncAudioFinished(false);
      syncTime();
      intervalId = setInterval(syncTime, 50);
    };

    const handlePauseOrEnd = () => {
      setIsPlaying(false);
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      syncTime();
      // Si el alumno ya escuchó el audio entero al menos una vez, una pausa
      // posterior se trata como «terminado» para volver a la fase 3 de la
      // barra inferior (play a la izquierda + «Empezar actividades»).
      if (hasBeenListenedRef.current) {
        audioContextRef.current?.syncAudioFinished(true);
      }
    };

    const markFinished = () => {
      onAudioFinishedRef.current?.();
      hasBeenListenedRef.current = true;
      // Notifica al `StoryAudioContext` para que la barra inferior pueda
      // reaccionar (p. ej. mover el play a la izquierda y revelar
      // «Empezar actividades»).
      audioContextRef.current?.markAudioFinished();
    };

    const handleEnded = () => {
      handlePauseOrEnd();
      setHasEnded(true);
      markFinished();
    };

    let hasMarkedFinished = false;
    const handleTimeUpdate = () => {
      syncTime();
      // Fallback: algunos navegadores no disparan 'ended' correctamente
      if (
        !hasMarkedFinished &&
        audio.duration > 0 &&
        !Number.isNaN(audio.duration) &&
        audio.currentTime >= audio.duration - 0.1
      ) {
        hasMarkedFinished = true;
        setHasEnded(true);
        markFinished();
      }
    };

    const handleLoadedMetadata = () => syncTime();

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePauseOrEnd);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('seeked', syncTime);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    const handleError = (e: Event) => {
      const target = e.target as HTMLAudioElement;
      const msg = target.error?.message ?? 'Unknown error';
      console.error('[KaraokePlayer] audio load/play error:', msg, { audioUrl, code: target.error?.code });
    };
    audio.addEventListener('error', handleError);

    return () => {
      if (intervalId) clearInterval(intervalId);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePauseOrEnd);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('seeked', syncTime);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl]);

  // Current word index
  const currentWordIndex = React.useMemo(
    () => timestamps.findIndex((t) => currentTime >= t.start && currentTime <= t.end),
    [timestamps, currentTime]
  );
  const currentWordIndexRef = React.useRef(currentWordIndex);
  currentWordIndexRef.current = currentWordIndex;

  // Play: subir enunciado/karaoke bajo el header (smooth salvo reduced motion).
  React.useEffect(() => {
    if (!isPlaying) return;
    const el = karaokeRootRef.current;
    if (!el) return;

    const useSmoothSnap = !reducedMotion;
    const suppressMs = useSmoothSnap ? KARAOKE_PLAY_SMOOTH_SUPPRESS_MS : 0;
    suppressWordLayoutScrollUntilRef.current = performance.now() + suppressMs;

    const snapBehavior: ScrollBehavior = useSmoothSnap ? 'smooth' : 'auto';
    const raf = { a: 0, b: 0 };
    raf.a = requestAnimationFrame(() => {
      raf.b = requestAnimationFrame(() => scrollKaraokeBlockBelowHeader(el, snapBehavior));
    });

    const postSnapTid = useSmoothSnap
      ? window.setTimeout(() => {
          const audio = audioRef.current;
          if (!audio || audio.paused) return;
          const idx = currentWordIndexRef.current;
          if (idx < 0) return;
          const w = wordRefs.current[idx] ?? null;
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          const root = el.closest('[data-story-scroll-container]') as HTMLElement | null;
          scrollLayoutIfWordClipped(w, root);
        }, suppressMs + 60)
      : undefined;

    return () => {
      cancelAnimationFrame(raf.a);
      cancelAnimationFrame(raf.b);
      if (postSnapTid !== undefined) window.clearTimeout(postSnapTid);
    };
  }, [isPlaying, reducedMotion]);

  // Reproducción: solo corregir si la palabra actual queda fuera de la zona visible (sin recentrar cada vez).
  React.useEffect(() => {
    if (!isPlaying || currentWordIndex < 0) return;
    const wordEl = wordRefs.current[currentWordIndex] ?? null;
    const scrollRoot = karaokeRootRef.current?.closest(
      '[data-story-scroll-container]'
    ) as HTMLElement | null;
    if (!wordEl || !scrollRoot) return;

    let id1 = 0;
    let id2 = 0;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        if (performance.now() < suppressWordLayoutScrollUntilRef.current) return;
        scrollLayoutIfWordClipped(wordEl, scrollRoot);
      });
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, [currentWordIndex, isPlaying]);

  const handlePlayPause = React.useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      // iOS Safari no preloada; load() en el gesto del usuario inicia la carga.
      // Solo llamar load() si el audio no está cargado (HAVE_NOTHING = 0).
      // Evita reiniciar a 0 cuando el usuario pausa y reanuda.
      if (audio.readyState === 0) {
        audio.load();
      }
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((err: unknown) => {
          console.error(
            '[KaraokePlayer] audio.play() failed:',
            err instanceof Error ? err.message : err,
            { audioUrl }
          );
          // Reintento tras 200ms por si el load() no había llegado a tiempo (móvil)
          setTimeout(() => {
            audio.play().catch(() => {});
          }, 200);
        });
      }
    }
  }, [isPlaying, audioUrl]);

  const handlePlayPauseRef = React.useRef(handlePlayPause);
  handlePlayPauseRef.current = handlePlayPause;

  // Register with context for bottom bar play button; hide local play button when registered.
  // Importante: dependemos de las funciones (estables vía `useCallback` en el provider) y NO del
  // objeto `audioContext` entero — si dependiéramos del objeto, cualquier `setState` del provider
  // recrearía el value y volvería a llamar `register()`, que resetea `hasAudioFinished` a `false`
  // (bucle: la barra inferior nunca pasaría a la fase «audio terminado»).
  const registerRef = audioContext?.register;
  const unregisterRef = audioContext?.unregister;
  React.useEffect(() => {
    if (!registerRef || !unregisterRef) return;
    registerRef({
      playPause: () => handlePlayPauseRef.current(),
    });
    return () => unregisterRef();
  }, [registerRef, unregisterRef]);

  // Audio ya escuchado en una sesión anterior: arranca en «terminado» (barra
  // inferior en fase 3). Va DESPUÉS del efecto de registro, que resetea
  // `hasAudioFinished` a false al montar. Si la prop llegara tarde con el
  // audio ya sonando, no interferimos: al pausar/terminar, `hasBeenListenedRef`
  // ya lo trata como escuchado.
  React.useEffect(() => {
    if (!initiallyListened) return;
    hasBeenListenedRef.current = true;
    const audio = audioRef.current;
    if (!audio || audio.paused) {
      audioContextRef.current?.syncAudioFinished(true);
    }
  }, [initiallyListened]);

  React.useEffect(() => {
    audioContext?.syncIsPlaying(isPlaying);
  }, [audioContext, isPlaying]);

  React.useEffect(() => {
    if (!audioContext) return;
    if (!transcriptUrl) {
      audioContext.syncKaraokeEntranceReady(true);
      return;
    }
    audioContext.syncKaraokeEntranceReady(!isLoadingTranscript);
  }, [audioContext, transcriptUrl, isLoadingTranscript]);

  /**
   * El botón local solo aparece si NO hay `StoryAudioContext`. Cuando lo
   * hay (caso normal en historias/conversaciones), el control vive en la barra
   * inferior — `KaraokePlayer` registra `playPause` en el contexto y la barra
   * lo dispara.
   */
  const showLocalPlayButton = !audioContext;

  /**
   * Auto-play al montar: tras un breve delay (para que termine la animación
   * de entrada de los recursos), arrancamos la reproducción. El efecto que
   * observa `isPlaying` se encarga del scroll suave que sube el karaoke bajo
   * el header (idéntico al «Empezar» de las conversaciones).
   */
  const autoPlayedRef = React.useRef(false);
  React.useEffect(() => {
    if (!autoPlay || autoPlayedRef.current) return;
    if (!audioUrl) return;
    if (transcriptUrl && isLoadingTranscript) return;
    autoPlayedRef.current = true;
    const id = window.setTimeout(() => {
      const audio = audioRef.current;
      if (!audio || !audio.paused) return;
      handlePlayPauseRef.current();
    }, 220);
    return () => window.clearTimeout(id);
  }, [autoPlay, audioUrl, transcriptUrl, isLoadingTranscript]);

  const transcriptEntranceActive =
    Boolean(transcriptUrl) && pageReady && !isLoadingTranscript;
  /** Cargando: visible; cargado: visible solo tras pageReady (transición violeta). */
  const transcriptMotionShown = isLoadingTranscript || pageReady;
  const transcriptHidden = { opacity: 0 };
  const transcriptShown = { opacity: 1 };

  return (
    <div
      ref={karaokeRootRef}
      className={cn(styles.karaokePlayer, className)}
      data-story-karaoke=""
    >
      {/* Transcript: motion fuera del div con overflow (misma estructura DOM que antes para el auto-scroll). */}
      {transcriptUrl && (
        <motion.div
          className={styles.karaokePlayer__transcriptMotionWrap}
          initial={transcriptHidden}
          animate={transcriptMotionShown ? transcriptShown : transcriptHidden}
          transition={karaokePlayEntranceTransition(
            reducedMotion,
            pageReady,
            transcriptEntranceActive
          )}
        >
          <div className={styles.karaokePlayer__transcript}>
            {isLoadingTranscript ? (
              <p className={styles.karaokePlayer__loading}>{t('vocabularyUi.karaokeLoadingTranscript')}</p>
            ) : timestamps.length > 0 ? (
              timestamps.map((item, i) => {
                if (item.word === '\n') {
                  return <br key={i} />;
                }
                const isPast = hasEnded || currentTime > item.end;
                const isCurrent = isPlaying && currentWordIndex === i;
                return (
                  <React.Fragment key={i}>
                    <span
                      ref={(el) => {
                        wordRefs.current[i] = el;
                      }}
                      className={cn(
                        styles.karaokePlayer__word,
                        isPast && styles['karaokePlayer__word--past'],
                        isCurrent && styles['karaokePlayer__word--current']
                      )}
                    >
                      {item.word}
                    </span>
                    {item.lineBreakAfter ? <br /> : null}
                    {!item.lineBreakAfter && i < timestamps.length - 1 && timestamps[i + 1]?.word !== '\n'
                      ? ' '
                      : ''}
                  </React.Fragment>
                );
              })
            ) : null}
          </div>
        </motion.div>
      )}

      {/* Controls — play button hidden when in bottom bar (audioContext) */}
      {showLocalPlayButton && (
        <div className={styles.karaokePlayer__controls}>
          <button
            type="button"
            className={styles.karaokePlayer__playButton}
            onClick={handlePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>
      )}

      {/* Hidden audio element */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={audioUrl}
        className={styles.karaokePlayer__audio}
        preload="auto"
      />
    </div>
  );
}
